/**
 * @fileoverview Mock backend for the Sales CRM HR Admin invitation commands.
 * @description Registers POST /sales/clients/{tenantId}/hr-admin-invitation and .../resend on the
 * feature's shared mock adapter, answering in the backend's envelopes with its error codes
 * (Confluence 3.5.11.31-.32). Both need an `Idempotency-Key` (428 / 4104) and are for the client's
 * owner or a Super Admin (403 / 2008); an archived client refuses them (409 / 4126). An HR Admin that
 * is already invited or active refuses another invite (409 / 4128) and an email that belongs to
 * someone else cannot be invited (409 / 1054). A resend needs a pending invitation (409 / 4129),
 * waits out the 30 second cooldown (429 / 1059) and stops after five resends (403 / 1060). Like the
 * other sales mocks it does not check MFA freshness. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { checkCommand, listClients } from './active-clients-mock-guard'
import {
  hasPendingInvitation,
  hrAdminOf,
  INVITATION_VALID_DAYS,
  RESEND_COOLDOWN_SECONDS,
  RESEND_LIMIT,
  saveHrAdmin,
} from './active-clients-mock-state'
import { bodyOf, failReply, okEnvelope } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { listSalesRoster } from './sales-roster-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { InviteHrAdminRequest } from '@/features/sales/types/sales-client.types'
import type { AxiosRequestConfig } from 'axios'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * @description Finds what the backend would refuse in an invitation's body with 400 / 0001.
 * @param {InviteHrAdminRequest} body - The request body.
 * @returns {string | null} What is wrong, or null when the body is valid.
 */
function invalidInvite(body: InviteHrAdminRequest): string | null {
  const names = [body.firstName, body.middleName ?? 'x', body.lastName]
  if (names.some((name) => !name.trim() || name.length > 100)) return 'A name is invalid.'
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(body.email ?? '')) return 'email must be an email.'
  if (!/^\+[1-9]\d{0,2}$/.test(body.countryCode ?? '')) return 'countryCode is invalid.'
  return /^\d{4,14}$/.test(body.mobileNumber ?? '') ? null : 'mobileNumber must be 4-14 digits.'
}

/**
 * @description Whether an email already belongs to someone: a roster user or another bank's HR Admin.
 * @param {string} email - The address to invite.
 * @returns {boolean} True when it cannot be invited.
 */
function emailTaken(email: string): boolean {
  const wanted = email.trim().toLowerCase()
  return (
    listSalesRoster().some((user) => user.email.toLowerCase() === wanted) ||
    listClients().some((tenant) => hrAdminOf(tenant)?.email.toLowerCase() === wanted)
  )
}

/**
 * @description Invites the bank's first user the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The invitation, or the backend's error.
 */
function inviteReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -2, 'INVITE_HR_ADMIN')
  if (Array.isArray(checked)) return checked
  const { caller, tenant } = checked
  const body = bodyOf(config) as unknown as InviteHrAdminRequest
  const invalid = invalidInvite(body)
  if (invalid) return failReply(400, '0001', invalid)
  const existing = hrAdminOf(tenant)
  if (existing && (existing.active || hasPendingInvitation(existing))) {
    return failReply(409, '4128', 'An HR Admin has already been invited')
  }
  if (emailTaken(body.email)) return failReply(409, '1054', 'That email cannot be invited')

  const now = new Date()
  const invitationId = crypto.randomUUID()
  const userId = crypto.randomUUID()
  const expiresAt = new Date(now.getTime() + INVITATION_VALID_DAYS * DAY_MS).toISOString()
  saveHrAdmin(tenant.id, {
    userId,
    name: [body.firstName, body.middleName, body.lastName].filter(Boolean).join(' '),
    email: body.email.trim(),
    phone: body.mobileNumber,
    invitedById: caller.id,
    invitedAt: now.toISOString(),
    invitationsSent: (existing?.invitationsSent ?? 0) + 1,
    active: false,
    invitation: {
      invitationId,
      sentAt: now.toISOString(),
      lastSentAt: now.toISOString(),
      expiresAt,
      resendCount: 0,
    },
  })
  return [
    201,
    okEnvelope({
      tenantId: tenant.id,
      userId,
      invitationId,
      expiresAt,
      hrAdminState: 'INVITED',
      deliveryStatus: 'QUEUED',
    }),
  ]
}

/**
 * @description Resends the HR Admin's pending invitation the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The invitation's new counters, or the backend's error.
 */
function resendReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -3, 'RESEND_HR_ADMIN_INVITE')
  if (Array.isArray(checked)) return checked
  const { tenant } = checked
  const hrAdmin = hrAdminOf(tenant)
  if (!hrAdmin?.invitation || !hasPendingInvitation(hrAdmin)) {
    return failReply(409, '4129', 'There is no pending invitation to resend')
  }
  const { invitation } = hrAdmin
  if (invitation.resendCount >= RESEND_LIMIT) {
    return failReply(403, '1060', 'The resend limit is reached')
  }
  const waitMs = Date.parse(invitation.lastSentAt) + RESEND_COOLDOWN_SECONDS * 1000 - Date.now()
  if (waitMs > 0) return failReply(429, '1059', 'Please wait before resending')

  const now = new Date()
  const resendCount = invitation.resendCount + 1
  const expiresAt = new Date(now.getTime() + INVITATION_VALID_DAYS * DAY_MS).toISOString()
  saveHrAdmin(tenant.id, {
    ...hrAdmin,
    invitation: { ...invitation, lastSentAt: now.toISOString(), expiresAt, resendCount },
  })
  return [
    200,
    okEnvelope({
      tenantId: tenant.id,
      invitationId: invitation.invitationId,
      expiresAt,
      resendCount,
      resendsRemaining: RESEND_LIMIT - resendCount,
      nextResendAt: new Date(now.getTime() + RESEND_COOLDOWN_SECONDS * 1000).toISOString(),
      deliveryStatus: 'QUEUED',
    }),
  ]
}

if (salesMockAdapter) {
  salesMockAdapter.onPost(/^\/sales\/clients\/[^/]+\/hr-admin-invitation$/).reply(inviteReply)
  salesMockAdapter
    .onPost(/^\/sales\/clients\/[^/]+\/hr-admin-invitation\/resend$/)
    .reply(resendReply)
}
